import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { listPurRecords, PurClientConfig } from "react-lib/apis/PUR/purApi";
import { listPurDocuments } from "react-lib/apis/PUR/purDocumentsApi";

import { MeERPScreenId } from "./MeERPScreenScaffold";
import { formatMeerpMessage, purErrorMessageId } from "./purPresentation";

type OverviewKey = "requests" | "orders" | "receipts" | "documents";
interface OverviewCard { key: OverviewKey; label: string; screen: MeERPScreenId }
interface OverviewState { count: number | null; errorId: string | null }
interface MeERPOverviewProps { client: PurClientConfig; onNavigate: (screen: MeERPScreenId) => void }

const CARDS: OverviewCard[] = [
  { key: "requests", label: "MEERP_OVERVIEW_PENDING_MR", screen: "PR01" },
  { key: "orders", label: "MEERP_OVERVIEW_PENDING_PO", screen: "GR01" },
  { key: "receipts", label: "MEERP_OVERVIEW_PENDING_GR", screen: "GR01" },
  { key: "documents", label: "MEERP_OVERVIEW_PENDING_DOCUMENT", screen: "RP01" },
];
const initialState = (): Record<OverviewKey, OverviewState> => ({
  requests: { count: null, errorId: null }, orders: { count: null, errorId: null },
  receipts: { count: null, errorId: null }, documents: { count: null, errorId: null },
});

const MeERPOverview: React.FC<MeERPOverviewProps> = ({ client, onNavigate }) => {
  const intl = useIntl();
  const t = (id: string) => formatMeerpMessage(intl, id);
  const [summary, setSummary] = useState<Record<OverviewKey, OverviewState>>(initialState);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true); setSummary(initialState());
    const load = async (key: OverviewKey, query: () => Promise<number>) => {
      try {
        const count = await query();
        if (active) setSummary((previous) => ({ ...previous, [key]: { count, errorId: null } }));
      } catch (error: unknown) {
        if (active) setSummary((previous) => ({ ...previous, [key]: { count: null, errorId: purErrorMessageId(error) } }));
      }
    };
    const one = (resource: "materialRequests" | "purchaseOrders" | "receipts", status: string) =>
      listPurRecords(client, resource, { limit: 1, offset: 0, status }).then((page) => page.count);
    Promise.all([
      load("requests", () => one("materialRequests", "SUBMITTED")),
      load("orders", async () => (await Promise.all(["APPROVED", "PARTIALLY_RECEIVED", "TO_RECEIVE"].map((status) => one("purchaseOrders", status)))).reduce((sum, count) => sum + count, 0)),
      load("receipts", () => one("receipts", "DRAFT")),
      load("documents", () => listPurDocuments(client, { limit: 1, offset: 0, verified: false }).then((page) => page.count)),
    ]).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, revision]);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  return <>
    <div className="card-meerp__page-heading">
      <div><h2>{t("MEERP_SCREEN_ER00")}</h2></div>
      <button type="button" className="card-meerp__button card-meerp__secondary" onClick={refresh}>{t("MEERP_REFRESH")}</button>
    </div>
    <p className="card-meerp__muted">{t("MEERP_OVERVIEW_LIVE_NOTE")}</p>
    <div className="card-meerp__modules" aria-busy={loading}>
      {CARDS.map((card) => {
        const result = summary[card.key];
        return <button type="button" className="card-meerp__module" key={card.key} onClick={() => onNavigate(card.screen)}>
          <strong>{t(card.label)}</strong>
          <span className="card-meerp__summary-value">{result.count === null ? "—" : intl.formatNumber(result.count)}</span>
          <span>{result.errorId ? t(result.errorId) : loading && result.count === null ? t("MEERP_LOADING") : t("MEERP_OVERVIEW_COUNT_NOTE")}</span>
        </button>;
      })}
    </div>
  </>;
};

export default MeERPOverview;
